/**
 * Backplan Store
 *
 * Manages Back Plan documents and their nodes, edges, swimlanes, and
 * milestone columns. A Back Plan is a reverse-chronological outcome
 * planning artifact: you start from a future desired state and work
 * backward to identify the conditions that must be true at each step.
 *
 * Edge creation goes through the create_backplan_edge RPC to prevent
 * dependency cycles. If the RPC returns error.code === 'cycle_detected',
 * the optimistic edge is rolled back.
 *
 * Per Build Contract: Supabase is source of truth. Zustand is a cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
} from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

export type BackplanViewMode = 'Tree' | 'Grid' | 'Split' | 'Canvas';

export type BackplanNodeTense = 'past' | 'present' | 'future';

export type BackplanNodeStatus = 'unknown' | 'true' | 'in-progress' | 'not-true';

export interface BackplanDocument {
  id: string;
  workspaceId: string;
  title: string;
  objective: string;
  futurePoint: string;
  definedSuccess: string | null;
  viewMode: BackplanViewMode;
  /** Optional: the Plan of Record (cycle) this back plan informs. */
  cycleId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type BackplanCellRelationship = 'sequential' | 'parallel';

export interface BackplanNode {
  id: string;
  backplanId: string;
  workspaceId: string;
  text: string;
  tense: BackplanNodeTense;
  isGoal: boolean;
  laneId: string | null;
  milestoneId: string | null;
  position: number;
  status: BackplanNodeStatus;
  /** Relationship to the node immediately above in the same cell (ignored for the topmost node). */
  cellRelationship: BackplanCellRelationship;
  tags: string[];
  /** Linked prioritization workspace item; null means planning-only until saved as item. */
  itemId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BackplanEdge {
  id: string;
  backplanId: string;
  workspaceId: string;
  fromNodeId: string;
  toNodeId: string;
  note: string | null;
  createdAt: string;
}

export interface BackplanSwimlane {
  id: string;
  backplanId: string;
  workspaceId: string;
  name: string;
  description: string | null;
  position: number;
  createdAt: string;
}

export interface BackplanMilestoneColumn {
  id: string;
  backplanId: string;
  workspaceId: string;
  name: string;
  position: number;
  description: string | null;
  condition: string | null;
  createdAt: string;
}

export type CreateEdgeError = 'cycle_detected' | 'self_loop' | 'duplicate' | 'network';

// ── Store interface ───────────────────────────────────────────

interface BackplanState {
  documents: BackplanDocument[];
  nodes: BackplanNode[];
  edges: BackplanEdge[];
  swimlanes: BackplanSwimlane[];
  milestones: BackplanMilestoneColumn[];
  isLoading: boolean;
  loadedBackplanId: string | null;
  error: string | null;

  // Document CRUD
  fetchDocuments: () => Promise<void>;
  fetchBackplan: (backplanId: string) => Promise<void>;
  createBackplan: (input: { title: string; objective?: string; futurePoint?: string }) => Promise<{ backplan: BackplanDocument | null; error?: string }>;
  updateBackplan: (id: string, updates: Partial<Pick<BackplanDocument, 'title' | 'objective' | 'futurePoint' | 'definedSuccess' | 'viewMode'>>) => Promise<{ success: boolean; error?: string }>;
  deleteBackplan: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Node CRUD
  createNode: (backplanId: string, input: Partial<BackplanNode>) => Promise<{ node: BackplanNode | null; error?: string }>;
  updateNode: (id: string, updates: Partial<BackplanNode>) => Promise<{ success: boolean; error?: string }>;
  deleteNode: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Edge CRUD (create goes through RPC for cycle detection)
  createEdge: (backplanId: string, fromNodeId: string, toNodeId: string, note?: string) => Promise<{ edge: BackplanEdge | null; error?: string; errorCode?: CreateEdgeError }>;
  deleteEdge: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Swimlanes
  createSwimlane: (backplanId: string, name: string) => Promise<{ swimlane: BackplanSwimlane | null; error?: string }>;
  updateSwimlane: (id: string, updates: { name?: string; position?: number }) => Promise<{ success: boolean; error?: string }>;
  deleteSwimlane: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Milestones
  createMilestone: (backplanId: string, name: string) => Promise<{ milestone: BackplanMilestoneColumn | null; error?: string }>;
  updateMilestone: (id: string, updates: Partial<BackplanMilestoneColumn>) => Promise<{ success: boolean; error?: string }>;
  deleteMilestone: (id: string) => Promise<{ success: boolean; error?: string }>;

  // Selectors
  getDocumentById: (id: string) => BackplanDocument | undefined;
  getNodesForBackplan: (backplanId: string) => BackplanNode[];
  getEdgesForBackplan: (backplanId: string) => BackplanEdge[];
  getSwimlanesForBackplan: (backplanId: string) => BackplanSwimlane[];
  getMilestonesForBackplan: (backplanId: string) => BackplanMilestoneColumn[];
}

// ── Helpers ───────────────────────────────────────────────────

function rowToDoc(r: Record<string, unknown>): BackplanDocument {
  return {
    id: r.id as string,
    workspaceId: r.workspace_id as string,
    title: (r.title as string) ?? 'Untitled Back Plan',
    objective: (r.objective as string) ?? '',
    futurePoint: (r.future_point as string) ?? '',
    definedSuccess: (r.defined_success as string) ?? null,
    viewMode: ((r.view_mode as string) ?? 'Tree') as BackplanViewMode,
    cycleId: (r.cycle_id as string) ?? null,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function rowToNode(r: Record<string, unknown>): BackplanNode {
  return {
    id: r.id as string,
    backplanId: r.backplan_id as string,
    workspaceId: r.workspace_id as string,
    text: (r.text as string) ?? '',
    tense: ((r.tense as string) ?? 'future') as BackplanNodeTense,
    isGoal: (r.is_goal as boolean) ?? false,
    laneId: (r.lane_id as string) ?? null,
    milestoneId: (r.milestone_id as string) ?? null,
    position: (r.position as number) ?? 0,
    status: ((r.status as string) ?? 'unknown') as BackplanNodeStatus,
    cellRelationship: ((r.cell_relationship as string) ?? 'parallel') as BackplanCellRelationship,
    tags: (r.tags as string[]) ?? [],
    itemId: (r.item_id as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function rowToEdge(r: Record<string, unknown>): BackplanEdge {
  return {
    id: r.id as string,
    backplanId: r.backplan_id as string,
    workspaceId: r.workspace_id as string,
    fromNodeId: r.from_node_id as string,
    toNodeId: r.to_node_id as string,
    note: (r.note as string) ?? null,
    createdAt: r.created_at as string,
  };
}

function rowToSwimlane(r: Record<string, unknown>): BackplanSwimlane {
  return {
    id: r.id as string,
    backplanId: r.backplan_id as string,
    workspaceId: r.workspace_id as string,
    name: (r.name as string) ?? 'Lane',
    description: (r.description as string) ?? null,
    position: (r.position as number) ?? 0,
    createdAt: r.created_at as string,
  };
}

function rowToMilestone(r: Record<string, unknown>): BackplanMilestoneColumn {
  return {
    id: r.id as string,
    backplanId: r.backplan_id as string,
    workspaceId: r.workspace_id as string,
    name: (r.name as string) ?? 'Milestone',
    position: (r.position as number) ?? 0,
    description: (r.description as string) ?? null,
    condition: (r.condition as string) ?? null,
    createdAt: r.created_at as string,
  };
}

// ── Store ─────────────────────────────────────────────────────

export const useBackplanStore = create<BackplanState>()((set, get) => ({
  documents: [],
  nodes: [],
  edges: [],
  swimlanes: [],
  milestones: [],
  isLoading: false,
  loadedBackplanId: null,
  error: null,

  fetchDocuments: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('backplan_documents')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('[BackplanStore] fetchDocuments error:', error);
      set({ isLoading: false, error: error.message });
      return;
    }
    set({
      documents: (data ?? []).map((r) => rowToDoc(r as Record<string, unknown>)),
      isLoading: false,
    });
  },

  fetchBackplan: async (backplanId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    set({ isLoading: true, error: null });

    const [nodesRes, edgesRes, swimlanesRes, milestonesRes] = await Promise.all([
      supabase.from('backplan_nodes').select('*').eq('backplan_id', backplanId),
      supabase.from('backplan_edges').select('*').eq('backplan_id', backplanId),
      supabase.from('backplan_swimlanes').select('*').eq('backplan_id', backplanId).order('position'),
      supabase.from('backplan_milestone_columns').select('*').eq('backplan_id', backplanId).order('position'),
    ]);

    set({
      nodes: (nodesRes.data ?? []).map((r) => rowToNode(r as Record<string, unknown>)),
      edges: (edgesRes.data ?? []).map((r) => rowToEdge(r as Record<string, unknown>)),
      swimlanes: (swimlanesRes.data ?? []).map((r) => rowToSwimlane(r as Record<string, unknown>)),
      milestones: (milestonesRes.data ?? []).map((r) => rowToMilestone(r as Record<string, unknown>)),
      loadedBackplanId: backplanId,
      isLoading: false,
    });
  },

  createBackplan: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { backplan: null, error: 'No Supabase client or workspace' };

    const { data, error } = await supabase
      .from('backplan_documents')
      .insert({
        id: generateUUID(),
        workspace_id: workspaceId,
        title: input.title || 'Untitled Back Plan',
        objective: input.objective ?? '',
        future_point: input.futurePoint ?? '',
      })
      .select('*')
      .single();

    if (error) {
      console.error('[BackplanStore] createBackplan error:', error);
      return { backplan: null, error: error.message };
    }

    const backplan = rowToDoc(data as Record<string, unknown>);
    set((state) => ({ documents: [backplan, ...state.documents] }));
    return { backplan };
  },

  updateBackplan: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.objective !== undefined) dbUpdates.objective = updates.objective;
    if (updates.futurePoint !== undefined) dbUpdates.future_point = updates.futurePoint;
    if (updates.definedSuccess !== undefined) dbUpdates.defined_success = updates.definedSuccess;
    if (updates.viewMode !== undefined) dbUpdates.view_mode = updates.viewMode;
    if (updates.cycleId !== undefined) dbUpdates.cycle_id = updates.cycleId;

    set((state) => ({
      documents: state.documents.map((d) => (d.id === id ? { ...d, ...updates } : d)),
    }));

    const { error } = await supabase.from('backplan_documents').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[BackplanStore] updateBackplan error:', error);
      await get().fetchDocuments();
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  deleteBackplan: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const snapshot = {
      documents: get().documents,
      nodes: get().nodes,
      edges: get().edges,
      swimlanes: get().swimlanes,
      milestones: get().milestones,
      loadedBackplanId: get().loadedBackplanId,
    };

    // Optimistic: document row plus in-memory graph (DB CASCADE removes child tables)
    set((state) => ({
      documents: state.documents.filter((d) => d.id !== id),
      nodes: state.nodes.filter((n) => n.backplanId !== id),
      edges: state.edges.filter((e) => e.backplanId !== id),
      swimlanes: state.swimlanes.filter((s) => s.backplanId !== id),
      milestones: state.milestones.filter((m) => m.backplanId !== id),
      loadedBackplanId: state.loadedBackplanId === id ? null : state.loadedBackplanId,
    }));

    const { error } = await supabase.from('backplan_documents').delete().eq('id', id);
    if (error) {
      console.error('[BackplanStore] deleteBackplan error:', error);
      set({
        documents: snapshot.documents,
        nodes: snapshot.nodes,
        edges: snapshot.edges,
        swimlanes: snapshot.swimlanes,
        milestones: snapshot.milestones,
        loadedBackplanId: snapshot.loadedBackplanId,
      });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createNode: async (backplanId, input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { node: null, error: 'No Supabase client or workspace' };

    const maxPos = get()
      .nodes.filter((n) => n.backplanId === backplanId)
      .reduce((max, n) => Math.max(max, n.position), -1);

    const { data, error } = await supabase
      .from('backplan_nodes')
      .insert({
        id: generateUUID(),
        backplan_id: backplanId,
        workspace_id: workspaceId,
        text: input.text ?? '',
        tense: input.tense ?? 'future',
        is_goal: input.isGoal ?? false,
        position: input.position ?? maxPos + 1,
        status: input.status ?? 'unknown',
        cell_relationship: input.cellRelationship ?? 'parallel',
        tags: input.tags ?? [],
      })
      .select('*')
      .single();

    if (error) {
      console.error('[BackplanStore] createNode error:', error);
      return { node: null, error: error.message };
    }

    const node = rowToNode(data as Record<string, unknown>);
    set((state) => ({ nodes: [...state.nodes, node] }));
    return { node };
  },

  updateNode: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.text !== undefined) dbUpdates.text = updates.text;
    if (updates.tense !== undefined) dbUpdates.tense = updates.tense;
    if (updates.isGoal !== undefined) dbUpdates.is_goal = updates.isGoal;
    if (updates.laneId !== undefined) dbUpdates.lane_id = updates.laneId;
    if (updates.milestoneId !== undefined) dbUpdates.milestone_id = updates.milestoneId;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.cellRelationship !== undefined) dbUpdates.cell_relationship = updates.cellRelationship;
    if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
    if (updates.itemId !== undefined) dbUpdates.item_id = updates.itemId;

    // §2.1 — DB write first; local state updated only on success
    const { error } = await supabase.from('backplan_nodes').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('[BackplanStore] updateNode error:', error);
      return { success: false, error: error.message };
    }

    set((state) => ({
      nodes: state.nodes.map((n) => (n.id === id ? { ...n, ...updates } : n)),
    }));
    return { success: true };
  },

  deleteNode: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prevNodes = get().nodes;
    const prevEdges = get().edges;

    // Remove node and any connected edges
    set((state) => ({
      nodes: state.nodes.filter((n) => n.id !== id),
      edges: state.edges.filter((e) => e.fromNodeId !== id && e.toNodeId !== id),
    }));

    const { error } = await supabase.from('backplan_nodes').delete().eq('id', id);
    if (error) {
      console.error('[BackplanStore] deleteNode error:', error);
      set({ nodes: prevNodes, edges: prevEdges });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createEdge: async (backplanId, fromNodeId, toNodeId, note) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { edge: null, error: 'No Supabase client or workspace' };

    // Optimistic add
    const tempId = generateUUID();
    const tempEdge: BackplanEdge = {
      id: tempId,
      backplanId,
      workspaceId,
      fromNodeId,
      toNodeId,
      note: note ?? null,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({ edges: [...state.edges, tempEdge] }));

    const { data, error } = await supabase.rpc('create_backplan_edge', {
      p_backplan_id: backplanId,
      p_workspace_id: workspaceId,
      p_from_node_id: fromNodeId,
      p_to_node_id: toNodeId,
      p_note: note ?? null,
    });

    const result = data as { id?: string; error?: string } | null;

    if (error || result?.error) {
      // Roll back optimistic add
      set((state) => ({ edges: state.edges.filter((e) => e.id !== tempId) }));
      const errorCode = result?.error as CreateEdgeError | undefined;
      const message = result?.error
        ? String(result.error)
        : error?.message ?? 'Failed to create edge';
      console.error('[BackplanStore] createEdge error:', message);
      return { edge: null, error: message, errorCode: errorCode ?? 'network' };
    }

    // Replace temp with real ID
    const realEdge: BackplanEdge = { ...tempEdge, id: result!.id! };
    set((state) => ({
      edges: state.edges.map((e) => (e.id === tempId ? realEdge : e)),
    }));
    return { edge: realEdge };
  },

  deleteEdge: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const prev = get().edges;
    set((state) => ({ edges: state.edges.filter((e) => e.id !== id) }));

    const { error } = await supabase.from('backplan_edges').delete().eq('id', id);
    if (error) {
      console.error('[BackplanStore] deleteEdge error:', error);
      set({ edges: prev });
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  createSwimlane: async (backplanId, name) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { swimlane: null, error: 'No Supabase client or workspace' };

    const maxPos = get()
      .swimlanes.filter((s) => s.backplanId === backplanId)
      .reduce((max, s) => Math.max(max, s.position), -1);

    const { data, error } = await supabase
      .from('backplan_swimlanes')
      .insert({ id: generateUUID(), backplan_id: backplanId, workspace_id: workspaceId, name, position: maxPos + 1 })
      .select('*')
      .single();

    if (error) return { swimlane: null, error: error.message };
    const swimlane = rowToSwimlane(data as Record<string, unknown>);
    set((state) => ({ swimlanes: [...state.swimlanes, swimlane] }));
    return { swimlane };
  },

  updateSwimlane: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };
    set((state) => ({ swimlanes: state.swimlanes.map((s) => (s.id === id ? { ...s, ...updates } : s)) }));
    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    const { error } = await supabase.from('backplan_swimlanes').update(dbUpdates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  deleteSwimlane: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };
    const prev = get().swimlanes;
    set((state) => ({ swimlanes: state.swimlanes.filter((s) => s.id !== id) }));
    const { error } = await supabase.from('backplan_swimlanes').delete().eq('id', id);
    if (error) { set({ swimlanes: prev }); return { success: false, error: error.message }; }
    return { success: true };
  },

  createMilestone: async (backplanId, name) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { milestone: null, error: 'No Supabase client or workspace' };

    const maxPos = get()
      .milestones.filter((m) => m.backplanId === backplanId)
      .reduce((max, m) => Math.max(max, m.position), -1);

    const { data, error } = await supabase
      .from('backplan_milestone_columns')
      .insert({ id: generateUUID(), backplan_id: backplanId, workspace_id: workspaceId, name, position: maxPos + 1 })
      .select('*')
      .single();

    if (error) return { milestone: null, error: error.message };
    const milestone = rowToMilestone(data as Record<string, unknown>);
    set((state) => ({ milestones: [...state.milestones, milestone] }));
    return { milestone };
  },

  updateMilestone: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.position !== undefined) dbUpdates.position = updates.position;
    if (updates.description !== undefined) dbUpdates.description = updates.description;
    if (updates.condition !== undefined) dbUpdates.condition = updates.condition;

    set((state) => ({ milestones: state.milestones.map((m) => (m.id === id ? { ...m, ...updates } : m)) }));
    const { error } = await supabase.from('backplan_milestone_columns').update(dbUpdates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  deleteMilestone: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'No Supabase client' };
    const prev = get().milestones;
    set((state) => ({ milestones: state.milestones.filter((m) => m.id !== id) }));
    const { error } = await supabase.from('backplan_milestone_columns').delete().eq('id', id);
    if (error) { set({ milestones: prev }); return { success: false, error: error.message }; }
    return { success: true };
  },

  getDocumentById: (id) => get().documents.find((d) => d.id === id),
  getNodesForBackplan: (backplanId) => get().nodes.filter((n) => n.backplanId === backplanId),
  getEdgesForBackplan: (backplanId) => get().edges.filter((e) => e.backplanId === backplanId),
  getSwimlanesForBackplan: (backplanId) =>
    get().swimlanes.filter((s) => s.backplanId === backplanId).sort((a, b) => a.position - b.position),
  getMilestonesForBackplan: (backplanId) =>
    get().milestones.filter((m) => m.backplanId === backplanId).sort((a, b) => a.position - b.position),
}));
